import { readPdfImportRequest, pdfImportRequestDays } from 'semshared/offer/pdf-import';
import { PdfImportBilling, PdfImportRequestDetails } from './pdf-import-request';
import React from 'react';
import _ from 'lodash';
import moment from 'moment';
import { Link } from 'react-router-dom';
import {
  Box,
  Grid,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import i18next from 'i18next';
import SimpleTable from 'components/table/SimpleTable';

import {
  EOfferSource,
  EOfferStatus,
  WLOfferListViewFragment,
  WLOfferVersionListViewFragment,
} from '__generated__/graphql';

import SimpleAccordion from 'components/SimpleAccordion';
import { FormatDate, formatDate } from 'components/DateTime';

import { userSelector } from 'helper/security';

interface OfferInputViewProps {
  data: WLOfferListViewFragment;
  version: WLOfferVersionListViewFragment;
  /** SEM editor period. Overrides the stored request snapshot while the hotel is editing days. */
  periodStartDate?: string | null;
  periodEndDate?: string | null;
}

/** Stored request days plus any later day the hotel added, so the list reaches the shown end date. */
function widgetRequestDays<T extends { day: number }>(days: readonly T[], startDate: string, endDate: string): T[] {
  const storedDays = days.filter(day => day.day >= 0);
  const start = moment.utc(startDate, 'YYYY-MM-DD');
  const end = moment.utc(endDate, 'YYYY-MM-DD');
  const coveredThrough = start.isValid() && end.isValid() ? Math.max(0, end.diff(start, 'days')) : -1;
  const lastDay = Math.max(coveredThrough, ...storedDays.map(day => day.day));
  if (!Number.isFinite(lastDay) || lastDay < 0) return storedDays;

  const dayByIndex = new Map(storedDays.map(day => [day.day, day]));
  const requestDays: T[] = [];
  for (let dayIndex = 0; dayIndex <= lastDay; dayIndex++) {
    const storedDay = dayByIndex.get(dayIndex);
    if (storedDay) {
      requestDays.push(storedDay);
      continue;
    }
    // No customer-request row exists for a hotel-added day. Show the day with empty details.
    requestDays.push({
      day: dayIndex,
      totalGuests: 0,
      overnightGuests: 0,
      occupancy: [],
      addons: [],
    } as unknown as T);
  }
  return requestDays;
}

/**
 * Displays offer input data (customer request details).
 * Shows different layouts for WIDGET (input) vs LISTER (inputLister) sources.
 * Hides customer data based on permissions and offer status.
 */
export default function OfferInputView(props: OfferInputViewProps) {
  const user = userSelector()!;
  const pdfRequest = readPdfImportRequest(props.version.extRefData, {
    company: props.version.client.billingCompany || '',
    firstname: props.version.client.billingFirstname || '',
    lastname: props.version.client.billingLastname || '',
    address: props.version.client.billingAddress || '',
    zip: props.version.client.billingZip || '',
    city: props.version.client.billingCity || '',
    country: props.version.client.billingCountry || '',
  });
  const unknown = pdfRequest ? 'Unbekannt' : '';

  const InlineText = ({ children }: { children: React.ReactNode }) => <Box component="span">{children}</Box>;
  const formatDayHeader = (startDate: any, dayIndex: number) => {
    const dayDate = formatDate(moment.utc(startDate, 'YYYY-MM-DD').add(dayIndex, 'days').toDate());
    return `${i18next.t('offer-input-header-day')} ${dayIndex + 1} - ${dayDate}`;
  };

  const _canSeeCustomerData = () => {
    if (props.data.source === EOfferSource.WIDGET) return true;
    if (user.isSeminargo) return true;
    if (props.data.status === EOfferStatus.APPROVED) return true;
    return false;
  };

  return <Grid container>
    {/* Premium/Classic Customer Information */}
    {(props.version.input || props.version.inputLister) && props.data.publicCompany && (
      <Grid item xs={12} sx={{ padding: 2, backgroundColor: '#f5f5f5', borderRadius: 1, marginBottom: 2 }}>
        <Typography variant="body2" sx={{ color: 'primary.main' }}>
          {props.data.process === 'PREMIUM' ? (
            <>
              <strong>{i18next.t('offer-input-premium-customer-title')}:</strong> {i18next.t('offer-input-premium-customer-note')}
            </>
          ) : (
            <>
              <strong>{i18next.t('offer-input-classic-customer-title')}:</strong> {i18next.t('offer-input-classic-customer-note')}
            </>
          )}
        </Typography>
      </Grid>
    )}
    {!props.version.input && !props.version.inputLister && (
      <Grid item xs={6}>
        {i18next.t('offer-input-not-available')}
      </Grid>
    )}
    {props.version.input && (
      <>
        <Grid item xs={6}>
          <Grid container>
            {_canSeeCustomerData() && (
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={8} sx={{ padding: 1 }}>
                    <Typography variant="h6">{i18next.t('offer-input-header-email')}</Typography>
                    {props.version.client.email || unknown}
                  </Grid>
                  <Grid item xs={4} sx={{ padding: 1 }}>
                    <Typography variant="h6">{i18next.t('offer-input-header-phone')}</Typography>
                    {props.version.client.phone || (pdfRequest ? 'Unbekannt' : '-')}
                  </Grid>
                </Grid>
              </Grid>
            )}
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={4} sx={{ padding: 1 }}>
                  <Typography variant="h6">{i18next.t('offer-input-header-start')}</Typography>
                  <FormatDate date={props.periodStartDate || props.version.input!.startDate} />
                </Grid>
                <Grid item xs={4} sx={{ padding: 1 }}>
                  <Typography variant="h6">{i18next.t('offer-input-header-end')}</Typography>
                  <FormatDate date={props.periodEndDate || props.version.input!.endDate} />
                </Grid>
                <Grid item xs={4} sx={{ padding: 1 }}>
                  <Typography variant="h6">{i18next.t('offer-input-header-servicetype')}</Typography>
                  {props.version.input!.serviceType?.header}
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={12} sx={{ padding: 1 }}>
                  <Typography variant="h6">{i18next.t('offer-input-header-comment')}</Typography>
                  <Typography sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{props.version.input!.comment || '-'}</Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sx={{ padding: 1 }}>
              <Divider />
            </Grid>
            <Grid item xs={12} sx={{ padding: 1 }}>
              <Typography variant="h5">{i18next.t('offer-input-header-company-data')}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Grid container>
                {_canSeeCustomerData() && (
                  <>
                    <Grid item xs={4} sx={{ padding: 1 }}>
                      <Typography variant="h6">{i18next.t('offer-input-header-name')}</Typography>
                      {[props.version.client.firstname, props.version.client.lastname].filter(Boolean).join(' ') || unknown}
                    </Grid>
                  </>
                )}
                <Grid item xs={4} sx={{ padding: 1 }}>
                  <Typography variant="h6">{i18next.t('offer-input-header-company')}</Typography>
                  {props.version.client.company || unknown}
                </Grid>
                {_canSeeCustomerData() && (
                  <>
                    <Grid item xs={4} sx={{ padding: 1 }}>
                      <Typography variant="h6">{i18next.t('offer-input-header-address')}</Typography>
                      {props.version.client.address || unknown}
                      <br />
                      {[props.version.client.zip, props.version.client.city].filter(Boolean).join(' ') || unknown}
                      <br />
                      {props.version.client.country || unknown}
                    </Grid>
                  </>
                )}
              </Grid>
            </Grid>
            {_canSeeCustomerData() && (
              <>
                <Grid item xs={12} sx={{ padding: 1 }}>
                  <Divider />
                </Grid>
                <Grid item xs={12} sx={{ padding: 1 }}>
                  <Typography variant="h5">{i18next.t('offer-input-header-billing-data')}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Grid container>
                    <Grid item xs={4} sx={{ padding: 1 }}>
                      <Typography variant="h6">{i18next.t('offer-input-header-name')}</Typography>
                      {[props.version.client.billingFirstname, props.version.client.billingLastname].filter(Boolean).join(' ') || unknown}
                    </Grid>
                    <Grid item xs={4} sx={{ padding: 1 }}>
                      <Typography variant="h6">{i18next.t('offer-input-header-company')}</Typography>
                      {props.version.client.billingCompany || unknown}
                    </Grid>
                    <Grid item xs={4} sx={{ padding: 1 }}>
                      <Typography variant="h6">{i18next.t('offer-input-header-address')}</Typography>
                      {props.version.client.billingAddress || unknown}
                      <br />
                      {props.version.client.billingZip} {props.version.client.billingCity}
                      <br />
                      {props.version.client.billingCountry || unknown}
                    </Grid>
                  </Grid>
                </Grid>
              </>
            )}

            <Divider sx={{ marginTop: 2, marginBottom: 2 }} />
          </Grid>
        </Grid>
        <Grid item xs={6}>
          <Grid container>
            <Grid item xs={12} sx={{ padding: 1 }}>
              <SimpleAccordion autoOpen={true} header={String(i18next.t('offer-input-header-preday'))}>
                <Grid container>
                  <Grid item xs={4}>
                    <Typography variant="subtitle2">{i18next.t('offer-input-header-guests')}</Typography>
                    {props.version.input!.prevdayGuests}
                  </Grid>
                </Grid>
              </SimpleAccordion>
            </Grid>
            {widgetRequestDays(
              props.version.input!.days,
              props.periodStartDate || props.version.input!.startDate,
              props.periodEndDate || props.version.input!.endDate,
            ).map((day, index) => (
              <Grid key={index} item xs={12} sx={{ padding: 1 }}>
                <SimpleAccordion
                  autoOpen={true}
                  header={formatDayHeader(props.periodStartDate || props.version.input!.startDate, day.day)}
                >
                  <Grid container>
                    <Grid item xs={12}>
                      <Grid container>
                        <Grid item xs={4} sx={{ padding: 1 }}>
                          <Typography variant="subtitle2" component="span">{i18next.t('offer-input-header-guests')}:</Typography>{' '}
                          <InlineText>{day.totalGuests}</InlineText>
                        </Grid>
                        <Grid item xs={4} sx={{ padding: 1 }}>
                          <Typography variant="subtitle2" component="span">{i18next.t('offer-input-header-overnightguests')}:</Typography>{' '}
                          <InlineText>{day.overnightGuests}</InlineText>
                        </Grid>
                        <Grid item xs={12} sx={{ padding: 1 }}>
                          <Typography variant="subtitle2">{i18next.t('offer-input-header-rooms')}:</Typography>
                          <SimpleTable
                            fullWidth={false}
                            headers={[
                              i18next.t('offer-occupancy-header-room'),
                              i18next.t('offer-occupancy-header-occupancy'),
                              i18next.t('offer-occupancy-header-seating'),
                            ]}
                            rows={day.occupancy.map((row, index) => [
                              row.room,
                              i18next.t(`enums-EOfferRoomOccupancy-${row.occupancy}`),
                              i18next.t(`enums-EOfferRoomSeating-${row.seating}`),
                            ])}
                          />
                        </Grid>
                        <Grid item xs={12} sx={{ padding: 1 }}>
                          <Typography variant="subtitle2">{i18next.t('offer-input-header-addons')}:</Typography>
                          {day.addons.length === 0 && <Typography variant="body2">{i18next.t('offer-input-no-addons')}</Typography>}
                          {day.addons.length > 0 && (
                            <SimpleTable
                              fullWidth={false}
                              headers={[i18next.t('offer-addons-header-name'), i18next.t('offer-addons-header-count')]}
                              rows={day.addons.map((row, index) => [
                                <InlineText>
                                  {row.product && <Link to={`/products/product/${row.product.id}`}>{row.product.name}</Link>}
                                  {row.facility && <Link to={`/products/facility/${row.facility?.id}`}>{row.facility?.name}</Link>}
                                </InlineText>,
                                row.count,
                              ])}
                            />
                          )}
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </SimpleAccordion>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </>
    )}
    {props.version.inputLister && (
      <>
        <Grid item xs={6}>
          <Grid container>
            {_canSeeCustomerData() && (
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={8} sx={{ padding: 1 }}>
                    <Typography variant="h6">{i18next.t('offer-input-header-email')}</Typography>
                    {props.version.client.email || unknown}
                  </Grid>
                  <Grid item xs={4} sx={{ padding: 1 }}>
                    <Typography variant="h6">{i18next.t('offer-input-header-phone')}</Typography>
                    {props.version.client.phone || (pdfRequest ? 'Unbekannt' : '-')}
                  </Grid>
                </Grid>
              </Grid>
            )}
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={4} sx={{ padding: 1 }}>
                  <Typography variant="h6">{i18next.t('offer-input-header-start')}</Typography>
                  <FormatDate date={props.version.inputLister!.startDate} />
                </Grid>
                <Grid item xs={4} sx={{ padding: 1 }}>
                  <Typography variant="h6">{i18next.t('offer-input-header-end')}</Typography>
                  <FormatDate date={props.version.inputLister!.endDate} />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={12} sx={{ padding: 1 }}>
                  <Typography variant="h6">{i18next.t('offer-input-header-comment')}</Typography>
                  <Typography sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{props.version.inputLister!.comment || (pdfRequest ? 'Unbekannt' : '-')}</Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sx={{ padding: 1 }}>
              <Divider />
            </Grid>
            <Grid item xs={12} sx={{ padding: 1 }}>
              <Typography variant="h5">{i18next.t('offer-input-header-company-data')}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Grid container>
                {_canSeeCustomerData() && (
                  <>
                    <Grid item xs={4} sx={{ padding: 1 }}>
                      <Typography variant="h6">{i18next.t('offer-input-header-name')}</Typography>
                      {[props.version.client.firstname, props.version.client.lastname].filter(Boolean).join(' ') || unknown}
                    </Grid>
                  </>
                )}
                <Grid item xs={4} sx={{ padding: 1 }}>
                  <Typography variant="h6">{i18next.t('offer-input-header-company')}</Typography>
                  {props.version.client.company || unknown}
                </Grid>
                {_canSeeCustomerData() && (
                  <>
                    <Grid item xs={4} sx={{ padding: 1 }}>
                      <Typography variant="h6">{i18next.t('offer-input-header-address')}</Typography>
                      {props.version.client.address || unknown}
                      <br />
                      {[props.version.client.zip, props.version.client.city].filter(Boolean).join(' ') || unknown}
                      <br />
                      {props.version.client.country || unknown}
                    </Grid>
                  </>
                )}
              </Grid>
            </Grid>
            {_canSeeCustomerData() && (
              <>
                <Grid item xs={12} sx={{ padding: 1 }}>
                  <Divider />
                </Grid>
                <Grid item xs={12} sx={{ padding: 1 }}>
                  <Typography variant="h5">{i18next.t('offer-input-header-billing-data')}</Typography>
                </Grid>
                <Grid item xs={12}>
                  {pdfRequest ? (
                    <PdfImportBilling request={pdfRequest} />
                  ) : (
                    <Grid container>
                      <Grid item xs={4} sx={{ padding: 1 }}>
                        <Typography variant="h6">{i18next.t('offer-input-header-name')}</Typography>
                        {[props.version.client.billingFirstname, props.version.client.billingLastname].filter(Boolean).join(' ') || unknown}
                      </Grid>
                      <Grid item xs={4} sx={{ padding: 1 }}>
                        <Typography variant="h6">{i18next.t('offer-input-header-company')}</Typography>
                        {props.version.client.billingCompany || unknown}
                      </Grid>
                      <Grid item xs={4} sx={{ padding: 1 }}>
                        <Typography variant="h6">{i18next.t('offer-input-header-address')}</Typography>
                        {props.version.client.billingAddress || unknown}
                        <br />
                        {props.version.client.billingZip} {props.version.client.billingCity}
                        <br />
                        {props.version.client.billingCountry || unknown}
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </>
            )}

            <Divider sx={{ marginTop: 2, marginBottom: 2 }} />
          </Grid>
        </Grid>
        <Grid item xs={6}>
          <Grid container>
            {pdfRequest && (
              <Grid item xs={12} sx={{ p: 1 }}>
                <PdfImportRequestDetails
                  showComment={false}
                  request={{
                    ...pdfRequest,
                    days: pdfImportRequestDays({
                      request: pdfRequest,
                      event: {
                        title: '',
                        startDate: props.version.inputLister!.startDate,
                        endDate: props.version.inputLister!.endDate,
                        participants: null,
                      },
                    }),
                  }}
                />
              </Grid>
            )}
            {!pdfRequest && props.version.inputLister!.days.map((day, index) => (
              <Grid key={index} item xs={12} sx={{ padding: 1 }}>
                {day.day === -1 && (
                  <SimpleAccordion autoOpen={true} header={String(i18next.t('offer-input-header-preday'))}>
                    <Grid container>
                      <Grid item xs={4}>
                        <Typography variant="subtitle2">{i18next.t('offer-input-header-overnightguests-ez')}</Typography>
                        {day.overnightRoomsEZ}
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="subtitle2">{i18next.t('offer-input-header-overnightguests-dz')}</Typography>
                        {day.overnightRoomsDZ / 2}
                      </Grid>
                    </Grid>
                  </SimpleAccordion>
                )}
                {day.day >= 0 && (
                  <SimpleAccordion
                    autoOpen={true}
                    header={formatDayHeader(props.version.inputLister!.startDate, day.day)}
                  >
                    <Grid container>
                      <Grid item xs={12}>
                        <Grid container>
                          <Grid item xs={4} sx={{ padding: 1 }}>
                            <Typography variant="subtitle2" component="span">{i18next.t('offer-input-header-guests')}:</Typography>{' '}
                            <InlineText>{day.totalGuests}</InlineText>
                          </Grid>
                          <Grid item xs={4} sx={{ padding: 1 }}>
                            <Typography variant="subtitle2" component="span">{i18next.t('offer-input-header-overnightguests-ez')}:</Typography>{' '}
                            <InlineText>{day.overnightRoomsEZ}</InlineText>
                          </Grid>
                          <Grid item xs={4} sx={{ padding: 1 }}>
                            <Typography variant="subtitle2" component="span">{i18next.t('offer-input-header-overnightguests-dz')}:</Typography>{' '}
                            <InlineText>{day.overnightRoomsDZ / 2}</InlineText>
                          </Grid>
                          <Grid item xs={12} sx={{ padding: 1 }}>
                            <Typography variant="subtitle2">{i18next.t('offer-input-header-rooms')}:</Typography>
                            <SimpleTable
                              fullWidth={false}
                              headers={[
                                i18next.t('offer-occupancy-header-room'),
                                i18next.t('offer-occupancy-header-occupancy'),
                                i18next.t('offer-occupancy-header-seating'),
                                i18next.t('offer-occupancy-header-participants'),
                                i18next.t('offer-occupancy-header-requirements'),
                                i18next.t('offer-occupancy-header-technology'),
                              ]}
                              rows={day.occupancy.map(row => {
                                const requirements = [
                                  row.requireAirConditioning ? i18next.t('offer-occupancy-air') : null,
                                  row.requireDaylight ? i18next.t('offer-occupancy-daylight') : null,
                                  row.minRoomAreaSqm ? `${row.minRoomAreaSqm} m²` : null,
                                ].filter(Boolean);
                                const technology = [
                                  row.beamerCount ? `${row.beamerCount}x ${i18next.t('selector-lister-beamer')}` : null,
                                  row.flipchartCount ? `${row.flipchartCount}x ${i18next.t('selector-lister-flipchart')}` : null,
                                  row.pinboardCount ? `${row.pinboardCount}x ${i18next.t('selector-lister-pinnwand')}` : null,
                                ].filter(Boolean);
                                return [
                                  row.room,
                                  `${row.occupancyFrom} - ${row.occupancyTo}`,
                                  row.seating === 'OTHER' && row.seatingDescription ? (
                                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                      {i18next.t('offer-individual-seating')}
                                      <Tooltip
                                        title={
                                          <Box sx={{ maxWidth: 360, maxHeight: 240, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                                            {row.seatingDescription}
                                          </Box>
                                        }
                                      >
                                        <IconButton size="small" aria-label={String(i18next.t('offer-individual-seating'))}>
                                          <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  ) : (
                                    i18next.t(`enums-EOfferRoomSeating-${row.seating}`)
                                  ),
                                  row.seatCount ?? '-',
                                  requirements.length ? requirements.join(', ') : '-',
                                  technology.length ? technology.join(', ') : '-',
                                ];
                              })}
                            />
                          </Grid>
                          <Grid item xs={12} sx={{ padding: 1 }}>
                            <Typography variant="subtitle2">{i18next.t('offer-input-header-addons')}:</Typography>
                            {day.addons.length === 0 && <Typography variant="body2">{i18next.t('offer-input-no-addons')}</Typography>}
                            {day.addons.length > 0 && (
                              <SimpleTable
                                fullWidth={false}
                                headers={[i18next.t('offer-addons-header-name'), i18next.t('offer-addons-header-count')]}
                                rows={day.addons.map((row, index) => [
                                  <InlineText>{row.product && <Link to={`/products/product/${row.product.id}`}>{row.product.name}</Link>}</InlineText>,
                                  row.count,
                                ])}
                              />
                            )}
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </SimpleAccordion>
                )}
              </Grid>
            ))}
          </Grid>
        </Grid>
      </>
    )}
  </Grid>
}
